import { useEffect, useRef, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { RefreshCw, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { APP_BUILD_ID } from "@/lib/app-version";

const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes
const INITIAL_DELAY = 2 * 60 * 1000; // 2 minutes — give user time to settle

/**
 * Polls `/version.json` (written by the `versionJsonPlugin` in vite.config.ts
 * at production build time) every 5 minutes. When the served `buildId` no
 * longer matches the SHA baked into the running bundle, shows a small toast
 * prompting a reload.
 *
 * Mirrors apps/web's UpdateBanner (which polls the Next-side /api/version).
 * Mounted once at the SPA root.
 */
export function UpdateBanner() {
  const { t } = useLingui();
  const [newVersion, setNewVersion] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const checkVersion = async () => {
      try {
        const res = await fetch("/version.json", { cache: "no-store" });
        if (res.ok && mountedRef.current) {
          const { buildId } = (await res.json()) as { buildId?: string };
          if (buildId && buildId !== "dev" && buildId !== APP_BUILD_ID) {
            setNewVersion(true);
          }
        }
      } catch {
        // Ignore network errors — banner stays hidden.
      }
    };

    const initialTimer = setTimeout(checkVersion, INITIAL_DELAY);
    const interval = setInterval(checkVersion, POLL_INTERVAL);

    return () => {
      mountedRef.current = false;
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, []);

  if (!newVersion || dismissed) return null;

  // Below lg the fixed BottomTabBar (h-14 + safe area) owns the bottom
  // edge, so the banner sits above it instead of covering the tab labels.
  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 fixed bottom-[calc(4.5rem+env(safe-area-inset-bottom))] left-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 lg:bottom-6">
      <div className="bg-card border-border flex items-start gap-3 rounded-2xl border py-3 pr-1 pl-4 shadow-2xl">
        <div className="bg-muted mt-1 flex size-10 shrink-0 items-center justify-center rounded-full">
          <RefreshCw className="text-muted-foreground size-5" aria-hidden />
        </div>

        <div className="min-w-0 flex-1 py-1">
          <div className="text-foreground text-sm font-semibold">
            <Trans>Refresh to update</Trans>
          </div>
          <p className="text-muted-foreground mt-0.5 text-sm">
            <Trans>A new version of PontiMed is available.</Trans>
          </p>
          <Button size="sm" className="mt-2 min-h-11 px-4" onClick={() => window.location.reload()}>
            <Trans>Refresh</Trans>
          </Button>
        </div>

        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="text-muted-foreground hover:text-foreground flex size-11 shrink-0 items-center justify-center rounded-lg transition-colors"
          aria-label={t`Close`}
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
